import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ExternalLink, Globe, Loader2, Search, Send, Info } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg, inr } from "@/lib/api";
import { useMeta, MetaConnectCard } from "@/components/MetaPanel";
import { WebsitePlanPicker, BlueprintView } from "@/components/WebsitePlanner";
import RfqStepNew from "@/components/WebsiteProposal";
import UnifiedBuilder from "@/components/am/UnifiedBuilder";
import CampaignResults from "@/components/CampaignResults";
export { AccountsStep } from "@/components/AccountDetails";

export function Choice({ options, value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map(([v, l]) => (
        <button key={v} onClick={() => onChange(v)} data-testid={`${testid}-${v}`}
          className={`px-4 py-2 rounded-lg border text-sm transition-colors ${value === v ? "border-emerald-500 bg-emerald-500/10 text-emerald-500 font-semibold" : "border-slate-800 text-slate-300 hover:border-slate-600"}`}>{l}</button>
      ))}
    </div>
  );
}

export const Tip = ({ children }) => <p className="flex gap-2 text-xs text-slate-400 bg-slate-900 rounded-lg p-3"><Info className="h-4 w-4 shrink-0 text-cyan-400" /><span>{children}</span></p>;

const YN = [["yes", "Yes"], ["no", "No"]];
const QUESTIONS = [
  { k: "stage", q: "Where is your project today?", opts: [["upcoming", "Upcoming"], ["under_construction", "Under construction"], ["launched", "Already launched"]] },
  { k: "project_name", q: "What is the project name?", text: true },
  { k: "company_name", q: "What is your developer / company name?", text: true },
  { k: "project_type", q: "What type of project is it?", opts: [["Residential", "Residential"], ["Commercial", "Commercial"], ["Plotted", "Plotted development"], ["Mixed-Use", "Mixed-use"], ["Other", "Other"]] },
  { k: "has_website", q: "Does this project already have a website?", opts: YN, tip: "A website is where buyers read about your project and leave enquiries. Ads send people there." },
  { k: "website_url", q: "What is the website address?", text: true, when: (a) => a.has_website === "yes", verify: true },
  { k: "owns_domain", q: "Do you (your company) own and control this website and its domain?", opts: [["yes", "Yes, we own it"], ["agency", "Our agency controls it"], ["unsure", "Not sure"]], when: (a) => a.has_website === "yes",
    tip: "You should always own your domain and have the registrar login yourself. Never share that password with anyone, including LaunchX360." },
];

export function ProjectStep({ data, save }) {
  const a = data.plan.answers;
  const qs = QUESTIONS.filter((x) => !x.when || x.when(a));
  const [i, setI] = useState(0);
  const [checking, setChecking] = useState(false);
  const q = qs[Math.min(i, qs.length - 1)];
  const def = { project_name: data.project.name, company_name: data.organization.name };
  const val = a[q.k] ?? def[q.k] ?? "";
  const [draft, setDraft] = useState(val);
  useEffect(() => { setDraft(a[q.k] ?? def[q.k] ?? ""); }, [q.k]); // eslint-disable-line
  const verify = async () => {
    setChecking(true);
    try { const r = (await api.post("/launch/website-check", { url: draft })).data; await save({ answers: { website_url: r.url, website_verified: r.reachable } }); r.reachable ? toast.success(r.detail) : toast.error(r.detail); }
    catch (e) { toast.error(errMsg(e)); } finally { setChecking(false); }
  };
  const next = async () => { if (q.text) await save({ answers: { [q.k]: draft } }); setI(Math.min(i + 1, qs.length)); };
  if (i >= qs.length) return (
    <div className="space-y-3" data-testid="wizard-project-done"><div className="flex items-center gap-2 font-semibold text-emerald-500"><CheckCircle2 className="h-5 w-5" />Project basics saved</div>
      <Button variant="secondary" onClick={() => setI(0)} data-testid="wizard-project-edit">Review answers</Button></div>
  );
  return (
    <div className="space-y-4" data-testid="wizard-question">
      <div className="text-xs font-mono text-slate-500">Question {i + 1} of {qs.length}</div>
      <div className="text-lg font-semibold">{q.q}</div>
      {q.opts ? <Choice options={q.opts} value={a[q.k]} onChange={async (v) => { await save({ answers: { [q.k]: v } }); setI(i + 1); }} testid={`wizard-q-${q.k}`} />
        : <Input value={draft} onChange={(e) => setDraft(e.target.value)} data-testid={`wizard-q-${q.k}-input`} />}
      {q.verify && (
        <div className="flex items-center gap-2"><Button variant="secondary" onClick={verify} disabled={!draft || checking} data-testid="wizard-verify-website">{checking ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Globe className="h-4 w-4 mr-1" />}Check website</Button>
          {a.website_verified === true && <span className="text-xs text-emerald-500">Verified online</span>}{a.website_verified === false && <span className="text-xs text-rose-400">Not reachable</span>}</div>
      )}
      {q.tip && <Tip>{q.tip}</Tip>}
      <div className="flex gap-2"><Button variant="ghost" disabled={i === 0} onClick={() => setI(i - 1)} data-testid="wizard-back">Back</Button>
        {q.text && <Button onClick={next} disabled={!draft} data-testid="wizard-next">Next</Button>}</div>
    </div>
  );
}

const SCOPE = ["Mobile-friendly website", "Project information & amenities", "Photo gallery, floor plans & videos", "Location map & connectivity", "Enquiry forms & site-visit booking",
  "WhatsApp / contact buttons", "Privacy policy & legal disclosures", "Lead tracking & analytics", "Landing pages for ad campaigns"];

export function WebsiteStep({ data, save, refresh }) {
  const a = data.plan.answers;
  if (a.has_website === "yes") return <Tip>You already have a website{a.website_verified ? " and we verified it is online" : ""}. You can skip the website steps and move to accounts.</Tip>;
  const rec = data.recommendation;
  const strat = [["project_site", "A. Separate project website", "e.g. sunriseheights.com — own brand, focused ad landing pages, simple tracking."],
    ["company_page", "B. Project page on company website", "e.g. abcdevelopers.com/sunrise-heights — builds long-term company trust across projects."]];
  return (
    <div className="space-y-5">
      <div className="space-y-2"><div className="font-semibold">Do you already have a company website?</div><Choice options={YN} value={a.has_company_site} onChange={(v) => save({ answers: { has_company_site: v } })} testid="wizard-company-site" /></div>
      <div className="space-y-2"><div className="font-semibold">How many projects do you plan in the next few years?</div><Choice options={[["one", "Just this one"], ["few", "2-3"], ["many", "Many"]]} value={a.future_projects} onChange={(v) => save({ answers: { future_projects: v } })} testid="wizard-future-projects" /></div>
      <div className="grid md:grid-cols-2 gap-3">{strat.map(([k, t, d]) => (
        <button key={k} onClick={() => save({ answers: { website_choice_strategy: k } })} data-testid={`wizard-strategy-${k}`}
          className={`text-left rounded-xl border p-4 transition-colors ${a.website_choice_strategy === k ? "border-emerald-500 bg-emerald-500/5" : "border-slate-800 hover:border-slate-600"}`}>
          <div className="font-semibold flex items-center gap-2">{t}{rec.recommended === k && <span className="lx-ai-badge">RECOMMENDED</span>}</div><p className="text-xs text-slate-400 mt-1">{d}</p></button>))}</div>
      <Tip>Why we recommend this: {rec.why}</Tip>
      <p className="text-xs text-slate-500" data-testid="wizard-builder-moved">You will choose who builds the website in step 6, after reviewing the blueprint and prices.</p>
      <WebsitePlanPicker data={data} refresh={refresh} />
      <div><div className="font-semibold mb-2">What a good project website includes</div><ul className="grid sm:grid-cols-2 gap-1 text-xs text-slate-300">{SCOPE.map((s) => <li key={s} className="flex gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5" />{s}</li>)}</ul></div>
    </div>
  );
}

export function DomainStep({ data, save }) {
  const a = data.plan.answers;
  const [d, setD] = useState(a.domain_choice || data.domain_ideas[0] || "");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  if (a.has_website === "yes") return <Tip>You already have a website and domain, so this step is done.</Tip>;
  const check = async (x = d) => { setBusy(true); try { setRes((await api.get("/launch/domain-check", { params: { domain: x } })).data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const base = (a.domain_choice || d).includes(".") ? (a.domain_choice || d) : "yourdomain.com";
  return (
    <div className="space-y-4">
      <Tip>A domain is your website address. A project domain (sunriseheights.com) suits one project; a company domain (abcdevelopers.com) hosts all your projects.</Tip>
      <div className="flex flex-wrap gap-2">{data.domain_ideas.map((x) => <button key={x} onClick={() => { setD(x); check(x); }} className="px-3 py-1 rounded-full border border-slate-800 text-xs font-mono hover:border-emerald-500" data-testid={`wizard-domain-idea-${x}`}>{x}</button>)}</div>
      <div className="flex gap-2"><Input value={d} onChange={(e) => setD(e.target.value)} className="font-mono" data-testid="wizard-domain-input" />
        <Button variant="secondary" onClick={() => check()} disabled={busy} data-testid="wizard-domain-check">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}</Button></div>
      {res && (
        <div className="lx-card text-sm space-y-2" data-testid="wizard-domain-result">
          <div className="font-mono font-semibold">{res.domain}: <span className={res.status === "not_found" ? "text-emerald-500" : res.status === "registered" ? "text-rose-400" : "text-amber-600"}>{res.status === "not_found" ? "may be available" : res.status === "registered" ? "taken" : "unknown"}</span></div>
          <p className="text-xs text-slate-400">{res.detail} ({res.source}). Prices are shown only by the registrar.</p>
          <div className="flex gap-2"><a href={res.registrar_url} target="_blank" rel="noreferrer"><Button size="sm" variant="secondary" data-testid="wizard-domain-registrar"><ExternalLink className="h-3.5 w-3.5 mr-1" />Check & buy on GoDaddy</Button></a>
            <Button size="sm" onClick={() => save({ answers: { domain_choice: res.domain } })} data-testid="wizard-domain-choose">Use this domain</Button></div>
        </div>
      )}
      {a.domain_choice && <div className="text-sm">Chosen domain: <b className="font-mono" data-testid="wizard-domain-chosen">{a.domain_choice}</b></div>}
      <Tip>Buy it in your company's own registrar account for at least 1 year (subject to the registrar's terms). Then set up 3 suggested business emails with an email provider: info@{base.split("/")[0]}, sales@{base.split("/")[0]}, marketing@{base.split("/")[0]}. Never share registrar or email passwords or OTPs with anyone.</Tip>
    </div>
  );
}

const CHECK = [["names", "Company & project names"], ["logo_colours", "Logo & brand colours"], ["contacts", "Contact person, emails, phones, office address"], ["description", "Project description, configurations, amenities, approved pricing"],
  ["rera", "RERA registration & disclosures"], ["media", "Photos, renders, floor plans, brochures, videos"], ["location", "Location, Google Maps link, connectivity"], ["domain", "Preferred domain & registrar status"],
  ["pages", "Required website pages"], ["lead_forms", "Lead forms, site-visit booking, CRM needs"], ["legal", "Privacy policy & legal content"], ["other", "Anything else"]];

export function RequirementsStep({ data, save, refresh }) {
  const ck = data.plan.checklist;
  return (
    <div className="space-y-6">
    <div className="space-y-2" data-testid="wizard-checklist">
      <div className="font-semibold">Content you have ready</div>
      <Tip>Mark what you already have. Upload logo and colours in Organization → Branding, and photos and videos in Creative Studio.</Tip>
      {CHECK.map(([k, l]) => (
        <div key={k} className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-800 py-2">
          <span className="text-sm">{l}</span>
          <Choice options={[["available", "Have it"], ["pending", "Pending"], ["na", "Not needed"]]} value={ck[k]} onChange={(v) => save({ checklist: { [k]: v } })} testid={`wizard-check-${k}`} />
        </div>
      ))}
      <Textarea rows={2} placeholder="Other requirements or notes for the website team" defaultValue={data.plan.answers.website_notes || ""} onBlur={(e) => save({ answers: { website_notes: e.target.value } })} data-testid="wizard-website-notes" />
    </div>
    </div>
  );
}

export function BlueprintStep({ data, refresh }) {
  return <BlueprintView data={data} refresh={refresh} />;
}

export function RfqStep(props) {
  return <RfqStepNew {...props} />;
}

export function MetaStep({ canWrite }) {
  const [meta, reload] = useMeta();
  return (<div className="space-y-3"><MetaConnectCard meta={meta} reload={reload} canWrite={canWrite} />
    <Tip>Connecting opens Meta's own secure login. You choose your own ad account, Page and Instagram. LaunchX360 never uses the platform owner's accounts for your ads.</Tip></div>);
}

export function CampaignStep({ data }) {
  return <UnifiedBuilder projectId={data.project.id} />;
}

export function MonitorStep({ data }) {
  return (
    <div className="space-y-3"><CampaignResults projectId={data.project.id} />
      <Link to="/cost-of-selling"><Button variant="secondary">Open Cost of Selling</Button></Link></div>
  );
}

const OFF_CATS = ["Hoardings", "Newspaper", "Radio", "Events", "Metro", "Bus Shelter", "Digital Screens", "TV"];

export function OfflineStep({ data }) {
  const [f, setF] = useState({ category: "Hoardings", budget: "", deadline: "", requirements: "" });
  const [done, setDone] = useState(null);
  const create = async () => {
    try { const r = await api.post("/rfqs", { title: `${f.category} for ${data.project.name}`, category: f.category, project_id: data.project.id, budget: Number(f.budget) || 0, deadline: f.deadline, requirements: f.requirements, status: "Open" }); setDone(r.data); toast.success("RFQ created"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4" data-testid="wizard-offline">
      <Tip>Offline media (hoardings, newspapers, events) is bought from vendors. Create a request for quotes (RFQ), invite vendors, compare quotes and approve one in RFQ Center.</Tip>
      <Choice options={OFF_CATS.map((c) => [c, c])} value={f.category} onChange={(v) => setF({ ...f, category: v })} testid="wizard-offline-category" />
      <div className="grid sm:grid-cols-2 gap-3"><Input type="number" placeholder="Budget (INR)" value={f.budget} onChange={(e) => setF({ ...f, budget: e.target.value })} data-testid="wizard-offline-budget" />
        <Input type="date" value={f.deadline} onChange={(e) => setF({ ...f, deadline: e.target.value })} data-testid="wizard-offline-deadline" /></div>
      <Textarea rows={2} placeholder="Locations, sizes, quantities, dates" value={f.requirements} onChange={(e) => setF({ ...f, requirements: e.target.value })} data-testid="wizard-offline-reqs" />
      <div className="flex gap-2"><Button onClick={create} data-testid="wizard-offline-create">Create RFQ</Button>{done && <Link to="/rfqs"><Button variant="secondary">Open RFQ Center</Button></Link>}</div>
      <p className="text-xs text-slate-500">Open RFQs for this project: {data.open_rfqs}</p>
    </div>
  );
}
